import { NgModule } from '@angular/core';
import { abTestFeature } from '@campus/ab-test';
import {
  AlertReducer,
  AlgebrakitSessionReducer,
  BadgeReducer,
  BundleClassGroupReducer,
  BundleGroupReducer,
  BundlePersonReducer,
  BundleReducer,
  ClassGroupReducer,
  clearState,
  ConceptReducer,
  ContentStatusReducer,
  CredentialReducer,
  CurrentExerciseReducer,
  CurriculumReducer,
  CurriculumTreeConceptReducer,
  CurriculumTreeReducer,
  CurriculumTreeYearReducer,
  CustomSerializer,
  DiaboloPhaseReducer,
  DidacticFunctionReducer,
  EduContentBookReducer,
  EduContentProductTypeReducer,
  EduContentReducer,
  EduContentTocEduContentReducer,
  EduContentTocEvaluationReducer,
  EduContentTocLearningDomainFeature,
  EduContentTocLearningPlanGoalReducer,
  EduContentTocLoopReducer,
  EduContentTocNavigationFeature,
  EduContentTocReducer,
  EduNetReducer,
  EffectFeedbackReducer,
  EvaluationReducer,
  EvaluationScoreListReducer,
  EvaluationScoreListSubjectReducer,
  EvaluationScoreReducer,
  EvaluationSubjectGoalReducer,
  EvaluationSubjectReducer,
  FavoriteReducer,
  GoalEduContentTOCReducer,
  GoalReducer,
  GoalTopicReducer,
  GoalYearReducer,
  GroupPersonReducer,
  GroupReducer,
  HistoryReducer,
  LearnerProfileConceptReducer,
  LearnerProfileStrandReducer,
  LearningAreaReducer,
  LearningDomainReducer,
  LearningPlanGoalProgressReducer,
  LearningPlanGoalReducer,
  LevelReducer,
  LicenseReducer,
  LinkedPersonReducer,
  LoopReducer,
  MethodLevelReducer,
  MethodNewsReducer,
  MethodOptionCategoryReducer,
  MethodOptionReducer,
  MethodProductGroupProductReducer,
  MethodProductGroupReducer,
  MethodReducer,
  MethodStyleReducer,
  NewsCategoryNewsReducer,
  NewsCategoryReducer,
  NewsReducer,
  OlfLearningDomainReducer,
  PersonPreferenceReducer,
  PersonProductReducer,
  ProductContentReducer,
  ProductReducer,
  PurchaseProductReducer,
  PurchaseReducer,
  RecommendationReducer,
  ResultReducer,
  RoleReducer,
  RouterReducer,
  SchoolBillingAddressReducer,
  SchoolBillingReducer,
  SchoolProviderReducer,
  SchoolReducer,
  SchoolRoleMappingClassGroupReducer,
  SchoolRoleMappingReducer,
  SchoolTypeReducer,
  SectionContentReducer,
  SectionReducer,
  StudentContentStatusReducer,
  TaskClassGroupReducer,
  TaskEduContentReducer,
  TaskEduContentTaskGoalYearLevelReducer,
  TaskEvaluationSubjectReducer,
  TaskEvaluationSubjectScoreReducer,
  TaskGoalYearLevelReducer,
  TaskGroupReducer,
  TaskInstanceReducer,
  TaskLevelSettingReducer,
  TaskReducer,
  TaskStudentReducer,
  TeacherStudentReducer,
  UiReducer,
  UnlockedBoekeGroupReducer,
  UnlockedBoekeStudentReducer,
  UnlockedContentReducer,
  UnlockedCurriculumTreeReducer,
  UnlockedFreePracticeReducer,
  UserContentReducer,
  UserLessonReducer,
  UserReducer,
  YearReducer,
} from '@campus/dal';
import { ScopedPaletteFeature } from '@campus/features/theming/scoped-palette';
import { EffectsModule } from '@ngrx/effects';
import { NavigationActionTiming, StoreRouterConnectingModule } from '@ngrx/router-store';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { handleUndo } from 'ngrx-undo-v2';
import { environment } from '../environments/environment';

@NgModule({
  imports: [
    StoreModule.forRoot(
      {},
      {
        metaReducers: [handleUndo, clearState],
      }
    ),
    addFeature(RouterReducer),
    StoreRouterConnectingModule.forRoot({
      navigationActionTiming: NavigationActionTiming.PostActivation,
      serializer: CustomSerializer,
      stateKey: RouterReducer.NAME,
    }),
    addFeature(EduContentTocEvaluationReducer),
    addFeature(LearningAreaReducer),
    addFeature(LearningDomainReducer),
    addFeature(MethodReducer),
    addFeature(UserContentReducer),
    addFeature(UnlockedContentReducer),
    addFeature(StudentContentStatusReducer),
    addFeature(EduContentReducer),
    addFeature(BundleReducer),
    addFeature(UiReducer),
    addFeature(UnlockedBoekeGroupReducer),
    addFeature(UnlockedBoekeStudentReducer),
    addFeature(ContentStatusReducer),
    addFeature(UserReducer),
    addFeature(TaskReducer),
    addFeature(AlertReducer),
    addFeature(TaskInstanceReducer),
    addFeature(TaskEduContentReducer),
    addFeature(ResultReducer),
    addFeature(CurrentExerciseReducer),
    addFeature(TeacherStudentReducer),
    addFeature(LinkedPersonReducer),
    addFeature(CredentialReducer),
    addFeature(EffectFeedbackReducer),
    addFeature(FavoriteReducer),
    addFeature(HistoryReducer),
    addFeature(EduContentProductTypeReducer),
    addFeature(EduNetReducer),
    addFeature(SchoolTypeReducer),
    addFeature(YearReducer),
    addFeature(EduContentBookReducer),
    addFeature(DidacticFunctionReducer),
    addFeature(EduContentTocReducer),
    StoreModule.forFeature(EduContentTocNavigationFeature.feature),
    addFeature(OlfLearningDomainReducer),
    addFeature(DiaboloPhaseReducer),
    addFeature(LearningPlanGoalReducer),
    addFeature(LearningPlanGoalProgressReducer),
    addFeature(UserLessonReducer),
    addFeature(PersonPreferenceReducer),
    addFeature(SchoolReducer),
    addFeature(GroupReducer),
    addFeature(GroupPersonReducer),
    addFeature(ClassGroupReducer),
    addFeature(SchoolRoleMappingReducer),
    addFeature(SchoolRoleMappingClassGroupReducer),
    addFeature(RoleReducer),
    addFeature(SchoolReducer),
    addFeature(PersonProductReducer),
    addFeature(ProductReducer),
    addFeature(ProductContentReducer),
    addFeature(SectionReducer),
    addFeature(SectionContentReducer),
    addFeature(SchoolProviderReducer),
    addFeature(SchoolBillingReducer),
    addFeature(SchoolBillingAddressReducer),
    addFeature(EvaluationReducer),
    addFeature(EvaluationSubjectReducer),
    addFeature(EvaluationScoreReducer),
    addFeature(EvaluationScoreListSubjectReducer),
    addFeature(EvaluationSubjectGoalReducer),
    addFeature(EvaluationScoreListReducer),
    addFeature(NewsReducer),
    addFeature(NewsCategoryReducer),
    addFeature(NewsCategoryNewsReducer),
    addFeature(MethodNewsReducer),
    addFeature(MethodOptionReducer),
    addFeature(MethodOptionCategoryReducer),
    addFeature(MethodStyleReducer),
    addFeature(RecommendationReducer),
    addFeature(BadgeReducer),

    //for method pages
    addFeature(EduContentTocLearningPlanGoalReducer),
    addFeature(ConceptReducer),
    addFeature(GoalEduContentTOCReducer),

    //FOR RESULT PAGES
    addFeature(AlgebrakitSessionReducer),
    addFeature(LevelReducer),
    addFeature(UnlockedFreePracticeReducer),
    addFeature(CurriculumReducer),
    addFeature(UnlockedCurriculumTreeReducer),
    addFeature(LoopReducer),
    addFeature(CurriculumTreeReducer),
    addFeature(CurriculumTreeConceptReducer),
    addFeature(CurriculumTreeYearReducer),
    addFeature(LearnerProfileStrandReducer),
    addFeature(LearnerProfileConceptReducer),
    addFeature(TaskClassGroupReducer),
    addFeature(TaskGroupReducer),
    addFeature(TaskStudentReducer),
    addFeature(BundlePersonReducer),
    addFeature(BundleGroupReducer),
    addFeature(BundleClassGroupReducer),

    // FOR TASK PAGES
    addFeature(GoalTopicReducer),
    addFeature(GoalReducer),
    addFeature(GoalYearReducer),
    addFeature(TaskEvaluationSubjectScoreReducer),
    addFeature(TaskEvaluationSubjectScoreReducer),
    addFeature(TaskEvaluationSubjectReducer),
    addFeature(TaskGoalYearLevelReducer),
    addFeature(TaskLevelSettingReducer),
    addFeature(TaskEduContentTaskGoalYearLevelReducer),
    addFeature(MethodLevelReducer),
    addFeature(LicenseReducer),
    addFeature(EduContentTocLoopReducer),
    addFeature(EduContentTocEduContentReducer),
    StoreModule.forFeature(EduContentTocLearningDomainFeature.feature),

    // FOR SCHOOL ADMIN PAGES
    addFeature(MethodProductGroupReducer),
    addFeature(MethodProductGroupProductReducer),
    addFeature(PurchaseReducer),
    addFeature(PurchaseProductReducer),

    // FOR ADAPTIVE LEARNING PAGES
    addFeature(GoalEduContentTOCReducer),
    addFeature(ConceptReducer),

    // AB Test feature
    addFeature(abTestFeature),
    StoreModule.forFeature(ScopedPaletteFeature.feature),
    EffectsModule.forFeature([ScopedPaletteFeature.effects]),

    environment.enableDevTools
      ? StoreDevtoolsModule.instrument({
          maxAge: 25,
          logOnly: true,
          connectInZone: true,
        })
      : [],
  ],
})
export class AppStoreModule {}

export function addFeature(reducer) {
  return StoreModule.forFeature(reducer.NAME, reducer.reducer, {
    initialState: reducer.initialState,
  });
}
